/**
 * AdoptionSummaryCard Component
 * Displays key user adoption metrics summary
 *
 * Story 5.5: User Adoption Report Page Assembly
 */

import React from 'react';
import { clsx } from 'clsx';
import { Users, Target, Clock, TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { Card } from '../../common';
import type { UserAdoptionSummary } from '../../../services/reportApi';

export interface AdoptionSummaryCardProps {
  /** Summary data */
  data: UserAdoptionSummary;
  /** Loading state */
  isLoading?: boolean;
  /** Additional class names */
  className?: string;
}

/**
 * Loading skeleton
 */
const SummarySkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="p-4 rounded-lg bg-stone-100">
          <div className="h-4 bg-stone-200 rounded w-20 mb-2" />
          <div className="h-8 bg-stone-200 rounded w-16 mb-1" />
          <div className="h-3 bg-stone-200 rounded w-24" />
        </div>
      ))}
    </div>
  </div>
);

interface MetricCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | number;
  subtext?: string;
  color: 'blue' | 'green' | 'amber' | 'red';
}

const MetricCard: React.FC<MetricCardProps> = ({
  icon: Icon,
  label,
  value,
  subtext,
  color,
}) => {
  const colorStyles = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
  };

  return (
    <div className={clsx('p-4 rounded-lg', colorStyles[color])}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      {subtext && (
        <div className="text-xs mt-1 opacity-80">{subtext}</div>
      )}
    </div>
  );
};

/**
 * Adoption Summary Card Component
 */
export const AdoptionSummaryCard: React.FC<AdoptionSummaryCardProps> = ({
  data,
  isLoading = false,
  className,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Adoption Overview</h3>
        <SummarySkeleton />
      </Card>
    );
  }

  const {
    totalUsers,
    activatedUsers,
    activationRate,
    avgTimeToFirstOKR,
    avgFeaturesUsed,
    day30Retention,
    churnRate,
    fullyOnboardedRate,
  } = data;

  return (
    <Card className={clsx('p-6', className)}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-stone-900">Adoption Overview</h3>
        <span className="text-sm text-stone-500">{totalUsers} total users</span>
      </div>

      {/* Primary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MetricCard
          icon={Users}
          label="Activation Rate"
          value={`${activationRate}%`}
          subtext={`${activatedUsers} of ${totalUsers} users`}
          color={activationRate >= 80 ? 'green' : activationRate >= 60 ? 'amber' : 'red'}
        />
        <MetricCard
          icon={Target}
          label="Fully Onboarded"
          value={`${fullyOnboardedRate}%`}
          subtext="Completed all steps"
          color={fullyOnboardedRate >= 70 ? 'green' : fullyOnboardedRate >= 50 ? 'amber' : 'red'}
        />
        <MetricCard
          icon={Clock}
          label="Time to First OKR"
          value={`${avgTimeToFirstOKR}d`}
          subtext="Average days"
          color={avgTimeToFirstOKR <= 7 ? 'green' : avgTimeToFirstOKR <= 14 ? 'amber' : 'red'}
        />
        <MetricCard
          icon={day30Retention >= 70 ? TrendingUp : day30Retention >= 50 ? AlertTriangle : TrendingDown}
          label="30-Day Retention"
          value={`${day30Retention}%`}
          subtext={`${churnRate}% churn rate`}
          color={day30Retention >= 70 ? 'green' : day30Retention >= 50 ? 'amber' : 'red'}
        />
      </div>

      {/* Feature adoption indicator */}
      <div className="p-4 bg-stone-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-stone-700">Average Feature Adoption</span>
          <span className="text-sm font-bold text-stone-900">
            {avgFeaturesUsed.toFixed(1)} of 4 features
          </span>
        </div>
        <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
          <div
            className={clsx(
              'h-full rounded-full transition-all duration-500',
              avgFeaturesUsed >= 3 ? 'bg-green-500' :
              avgFeaturesUsed >= 2 ? 'bg-amber-500' : 'bg-red-500'
            )}
            style={{ width: `${(avgFeaturesUsed / 4) * 100}%` }}
          />
        </div>
      </div>
    </Card>
  );
};

export default AdoptionSummaryCard;
